import React, { useState, useRef, useCallback } from "react";

/**
 * LineChart - V4 styled SVG line chart
 *
 * @param {array} data - Array of {label, value, isActual?, meta?}
 * @param {number} height - Chart height in pixels (default: 120)
 * @param {function} formatValue - Value formatter for tooltip/display
 * @param {function} formatCompact - Compact value formatter for Y-axis (optional, uses formatValue if not provided)
 * @param {function} formatLabel - Label formatter for tooltip (optional, uses item.label)
 * @param {string} lineColor - Primary line color (default: success)
 * @param {string} negativeColor - Color when value < 0 (default: error)
 * @param {string} actualColor - Color for historical/actual data points (default: text-secondary)
 * @param {boolean} showGrid - Show vertical grid lines (default: true)
 * @param {boolean} showYAxis - Show Y-axis labels (default: true)
 * @param {boolean} showTooltip - Enable hover tooltips (default: true)
 * @param {boolean} showLabels - Show X-axis labels (default: true)
 * @param {array} yearMarkers - Months to show thicker grid lines (default: ["Jan"])
 * @param {number} labelInterval - Show every Nth label (default: auto-calculated)
 * @param {string} title - Optional chart title
 * @param {string} subtitle - Optional subtitle shown after title
 */
const LineChart = ({
  data = [],
  height = 120,
  formatValue = (v) => v?.toLocaleString() ?? "—",
  formatCompact,
  formatLabel,
  lineColor = "var(--v4-success)",
  negativeColor = "var(--v4-error)",
  actualColor = "var(--v4-text-secondary)",
  showGrid = true,
  showYAxis = true,
  showTooltip = true,
  showLabels = true,
  yearMarkers = ["Jan"],
  labelInterval,
  title,
  subtitle,
}) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const chartAreaRef = useRef(null);

  // Use formatValue for Y-axis if formatCompact not provided
  const compactFormat = formatCompact || formatValue;

  const handleMouseMove = useCallback(
    (e) => {
      if (!chartAreaRef.current || data.length < 2) return;

      const rect = chartAreaRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;

      const exactIndex = percentage * (data.length - 1);
      const nearestIndex = Math.round(exactIndex);
      const clampedIndex = Math.max(0, Math.min(data.length - 1, nearestIndex));

      setHoverIndex(clampedIndex);
    },
    [data.length]
  );

  const handleMouseLeave = useCallback(() => {
    setHoverIndex(null);
  }, []);

  // Filter out null values for min/max calculations
  const validData = data.filter((d) => d.value !== null && d.value !== undefined);

  if (validData.length === 0) {
    return (
      <div className="v4-line-chart">
        {title && (
          <div className="v4-line-chart__title">
            {title}
            {subtitle && <span className="v4-line-chart__subtitle">{subtitle}</span>}
          </div>
        )}
        <div className="v4-line-chart__empty">No data available</div>
      </div>
    );
  }

  const maxValue = Math.max(...validData.map((d) => d.value), 0);
  const minValue = Math.min(...validData.map((d) => d.value), 0);
  const range = maxValue - minValue || 1;

  // Calculate Y position for zero line
  const zeroY = minValue < 0 ? 100 - ((0 - minValue) / range) * 100 : null;

  // Helper to get line color based on whether points are actual/historical
  const getLineColor = (prevPoint, currPoint) => {
    if (prevPoint.isActual || currPoint.isActual) {
      return actualColor;
    }
    const isNegative = prevPoint.value < 0 || currPoint.value < 0;
    return isNegative ? negativeColor : lineColor;
  };

  // Calculate label interval if not provided
  const calcLabelInterval = labelInterval || Math.ceil(data.length / 12);

  // Get point color based on value and actual status
  const getPointColor = (point) => {
    if (point.isActual) return actualColor;
    return point.value < 0 ? negativeColor : lineColor;
  };

  return (
    <div className="v4-line-chart">
      {title && (
        <div className="v4-line-chart__title">
          {title}
          {subtitle && <span className="v4-line-chart__subtitle">{subtitle}</span>}
        </div>
      )}
      <div
        className="v4-line-chart__container"
        ref={chartAreaRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {showYAxis && (
          <div className="v4-line-chart__yaxis">
            <span style={{ top: 0 }}>{compactFormat(maxValue)}</span>
            {zeroY !== null && <span style={{ top: `${zeroY}%` }}>$0</span>}
            <span style={{ top: "100%" }}>{compactFormat(minValue)}</span>
          </div>
        )}
        <div className="v4-line-chart__chart">
          <svg
            viewBox="0 0 1000 100"
            preserveAspectRatio="none"
            className="v4-line-chart__svg"
            style={{ height }}
          >
            {/* Vertical grid lines */}
            {showGrid &&
              data.map((d, i) => {
                if (i === 0) return null;
                const x = (i / (data.length - 1)) * 1000;
                const isYearMarker = yearMarkers.some((marker) =>
                  d.label?.includes(marker)
                );
                return (
                  <line
                    key={`grid-${i}`}
                    x1={x}
                    y1="0"
                    x2={x}
                    y2="100"
                    className={`v4-line-chart__grid-line ${
                      isYearMarker ? "v4-line-chart__grid-line--year" : ""
                    }`}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}

            {/* Zero line if applicable */}
            {minValue < 0 && (
              <line
                x1="0"
                y1={100 - ((0 - minValue) / range) * 100}
                x2="1000"
                y2={100 - ((0 - minValue) / range) * 100}
                className="v4-line-chart__zero-line"
              />
            )}

            {/* Line segments */}
            {data.map((d, i) => {
              if (i === 0) return null;
              const prev = data[i - 1];
              // Skip if either point has null value
              if (prev.value === null || d.value === null) return null;

              const x1 = ((i - 1) / (data.length - 1)) * 1000;
              const y1 = 100 - ((prev.value - minValue) / range) * 100;
              const x2 = (i / (data.length - 1)) * 1000;
              const y2 = 100 - ((d.value - minValue) / range) * 100;

              return (
                <line
                  key={`line-${i}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={getLineColor(prev, d)}
                  className="v4-line-chart__path"
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}

            {/* Hover vertical line */}
            {showTooltip && hoverIndex !== null && (
              <line
                x1={(hoverIndex / (data.length - 1)) * 1000}
                y1="0"
                x2={(hoverIndex / (data.length - 1)) * 1000}
                y2="100"
                className="v4-line-chart__hover-line"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>

          {/* Data points overlay */}
          <div className="v4-line-chart__points" style={{ height }}>
            {data.map((d, i) => {
              if (d.value === null) return null;
              const isHovered = hoverIndex === i;

              return (
                <div
                  key={i}
                  className={`v4-line-chart__point ${
                    d.value < 0 ? "v4-line-chart__point--negative" : ""
                  } ${isHovered ? "v4-line-chart__point--hovered" : ""} ${
                    d.isActual ? "v4-line-chart__point--actual" : ""
                  }`}
                  style={{
                    left: `${(i / (data.length - 1)) * 100}%`,
                    top: `${100 - ((d.value - minValue) / range) * 100}%`,
                    "--point-color": getPointColor(d),
                  }}
                />
              );
            })}
          </div>

          {/* Hover tooltip */}
          {showTooltip &&
            hoverIndex !== null &&
            data[hoverIndex] &&
            data[hoverIndex].value !== null && (
              <div
                className="v4-line-chart__tooltip"
                style={{
                  left: `${(hoverIndex / (data.length - 1)) * 100}%`,
                  top: `${
                    100 - ((data[hoverIndex].value - minValue) / range) * 100
                  }%`,
                }}
              >
                <div className="v4-line-chart__tooltip-label">
                  {formatLabel
                    ? formatLabel(data[hoverIndex])
                    : data[hoverIndex].label}
                  {data[hoverIndex].isActual && (
                    <span className="v4-line-chart__tooltip-badge">actual</span>
                  )}
                </div>
                <div
                  className="v4-line-chart__tooltip-value"
                  style={{ color: getPointColor(data[hoverIndex]) }}
                >
                  {formatValue(data[hoverIndex].value)}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* X-axis labels */}
      {showLabels && (
        <div
          className="v4-line-chart__labels"
          style={{ marginLeft: showYAxis ? "68px" : 0 }}
        >
          {data
            .filter((_, i) => i % calcLabelInterval === 0)
            .map((d, i) => (
              <span key={i}>{d.label}</span>
            ))}
        </div>
      )}
    </div>
  );
};

export default LineChart;
